<template>
    <h2 class="display-sans-serif">Overview</h2>
    <div class="mt-6 mb-10 max-w-4xl">
        <AnalyticsFilters
            :show-date-filters="tabConfig?.overview_date_filters_enabled"
            :show-k-anonymization-error="showKAnonymizationError"
            @updated="filters = $event"
            @is-fetching-updated="isFetchingFilters = $event" />
        <h2 class="title-subsection mb-4 mx-2 mt-10 md:mt-10">Usage</h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
            <SimpleStatCard
                icon="bi-person-add"
                label="Registered Users"
                :value="usersData?.totals?.registered_users"
                :is-loading="isFetchingFilters || isFetchingUsers"
                tooltip="The number of users in your organization who have a Nadia account." />
            <SimpleStatCard
                icon="bi-graph-up-arrow"
                label="Active Users"
                :value="usersData?.totals?.active_users"
                :is-loading="isFetchingFilters || isFetchingUsers"
                tooltip="The number of users in your organization who have had at least one coaching conversation with Nadia." />
            <SimpleStatCard
                icon="bi bi-arrow-counterclockwise"
                label="Returning Users"
                :value="usersData?.totals?.return_users"
                :is-loading="isFetchingFilters || isFetchingUsers"
                tooltip="The number of users in your organization who have had two or more coaching conversations with Nadia, on different days." />
            <SimpleStatCard
                icon="bi-chat-left"
                label="Total Sessions"
                :value="sessionsData?.totals?.total_sessions"
                :is-loading="isFetchingFilters || isFetchingSessions"
                tooltip="The number of coaching conversations that users in your organization have had with Nadia." />
        </div>
        <div class="flex flex-col md:flex-row md:flex-wrap gap-5">
            <AnalyticsChart
                :title="usersChartTitle"
                :chart="charts.users"
                :is-loading="isFetchingFilters || isFetchingUsers"
                class="w-full md:w-auto md:min-w-80 md:max-w-110.5 h-64 md:h-80 md:flex-1"
                :tooltip="usersChartTooltip" />
            <AnalyticsChart
                title="Daily Sessions"
                :chart="charts.sessions"
                :is-loading="isFetchingFilters || isFetchingSessions"
                class="w-full md:w-auto md:min-w-80 md:max-w-110.5 h-64 md:h-80 md:flex-1"
                tooltip="Daily cumulative count of coaching conversations that users in your organization have had with Nadia." />
        </div>
        <InsightCard class="mt-5" :content="usageInsight?.content" :is-loading="isFetchingUsageInsight" />
    </div>
    <div v-if="isFetchingFeedbackStats || isFetchingFeedbackQuotes || (feedbackStats?.stats?.length ?? 0) > 0 || (feedbackQuotes?.quotes?.length ?? 0) > 0" class="max-w-4xl">
        <h2 class="title-subsection mb-4 mx-2">User Feedback</h2>
        <div v-if="isFetchingFeedbackStats || (feedbackStats?.stats?.length ?? 0) > 0" class="flex flex-col md:flex-row md:flex-wrap gap-5 mb-6">
            <ScoreCard
                v-for="(stat, index) in (feedbackStats?.stats ?? loadingPlaceholderStats).slice(0, 3)"
                :key="index"
                :title="stat.title"
                :value="stat.value"
                :caption="stat.caption"
                :color="stat.color"
                :is-loading="isFetchingFeedbackStats"
                class="flex-1" />
        </div>
        <template v-if="isFetchingFeedbackQuotes || (feedbackQuotes?.quotes?.length ?? 0) > 0">
            <div class="p-2 text-ink-muted body-small">What users are saying</div>
            <div class="flex flex-row gap-2.5 overflow-x-scroll pb-1 mb-10">
                <QuoteCard
                    v-for="(quoteObj, index) in feedbackQuotes?.quotes ?? loadingPlaceholderQuotes"
                    :key="index"
                    :quote="quoteObj.quote"
                    :is-loading="isFetchingFeedbackQuotes" />
            </div>
        </template>
    </div>
    <div v-if="tabConfig?.users_export_enabled">
        <h2 class="title-subsection">Export User List</h2>
        <p class="body-small mt-4">
            This will generate a <code>.xlsx</code> file listing the users in your organization and their activation status. Note: specific emails, names, locations, and functions
            are pulled from your HR system or SSO. If anything looks off, your EM can help confirm or update.
        </p>
        <a class="btn rounded-lgr mt-4" href="/client-analytics/export-users">
            <i class="bi bi-download mr-1" aria-hidden="true"></i>
            Download Users List
        </a>
    </div>
</template>

<script setup lang="ts">
import "chartjs-adapter-luxon";
import { useFetch } from "~vue/api";
import { cssColor } from "~vue/ChatHome/workiqTrendColors";
import type { ChartConfiguration } from "chart.js";
import _ from "lodash";
import { computed, inject, ref, watch } from "vue";

import AnalyticsChart from "../AnalyticsChart.vue";
import AnalyticsFilters from "../filters/AnalyticsFilters.vue";
import { getURLParamsFromFilters } from "../filters/helpers";
import InsightCard from "../InsightCard.vue";
import QuoteCard from "../QuoteCard.vue";
import ScoreCard from "../ScoreCard.vue";
import SimpleStatCard from "../SimpleStatCard.vue";
import type {
    ClientAnalyticsTabConfig,
    FeedbackQuotesResponse,
    FeedbackStat,
    FeedbackStatsResponse,
    NadiaInsightResponse,
    SelectedFilters,
    SessionsChartData,
    UsersChartData,
} from "../types";

const tabConfig = inject<ClientAnalyticsTabConfig>("analyticsTabConfig");

const filters = ref<SelectedFilters>({});
const isFetchingFilters = ref(true);

const sessionsUrl = ref("/api/client-analytics/sessions/");
const {
    data: sessionsData,
    isFetching: isFetchingSessions,
    statusCode: sessionsFetchStatusCode,
} = useFetch(sessionsUrl, { immediate: true, refetch: true }).json<SessionsChartData>();

const usersUrl = ref("/api/client-analytics/users/");
const { data: usersData, isFetching: isFetchingUsers, statusCode: usersFetchStatusCode } = useFetch(usersUrl, { immediate: true, refetch: true }).json<UsersChartData>();

const showKAnonymizationError = computed(() => usersFetchStatusCode.value === 451 || sessionsFetchStatusCode.value === 451);

watch(
    filters,
    (newFilters) => {
        const params = getURLParamsFromFilters(newFilters);
        const queryString = params.toString();
        sessionsUrl.value = queryString ? `/api/client-analytics/sessions/?${queryString}` : "/api/client-analytics/sessions/";
        usersUrl.value = queryString ? `/api/client-analytics/users/?${queryString}` : "/api/client-analytics/users/";
    },
    { deep: true },
);

const showCumulativeDailyChart = tabConfig?.overview_cumulative_users_chart_enabled ?? true;
const usersChartTitle = showCumulativeDailyChart ? "Daily Users" : "Monthly Active Users";
const usersChartTooltip = showCumulativeDailyChart
    ? "Daily cumulative counts of Active and Return Users in your organization.  Active Users are users who have had at least one coaching conversation with Nadia.  Return Users are users who have had at least two coaching conversations with Nadia, on different days."
    : "Count of Active Users in your organization by month.  Active Users are users who have had at least one coaching conversation with Nadia.";

const commonChartConfig = {
    options: {
        animation: false as const, // Changing filters before animation ends breaks the chart
        maintainAspectRatio: false,
        scales: {
            x: {
                type: "time" as const,
                grid: { display: false },
                ticks: {
                    font: { size: 14 },
                },
            },
            y: {
                title: { display: false },
                beginAtZero: true,
                border: { color: cssColor("--color-border", "#F5F5F5") },
                grid: {
                    color: () => cssColor("--color-border", "#F5F5F5"),
                    drawTicks: false,
                },
                ticks: {
                    font: { size: 14 },
                    padding: 6,
                },
            },
        },
        plugins: {
            legend: {
                position: "bottom" as const,
                align: "start" as const,
                reverse: true,
                labels: {
                    boxWidth: 14,
                    boxHeight: 14,
                    padding: 20, // Unfortunately, Chart.js doesn't have separate top/bottom/left/right padding options
                    borderRadius: 2,
                    useBorderRadius: true,
                    font: { size: 14 },
                },
            },
            datalabels: {
                display: false,
            },
        },
    },
};

const commonLineChartConfig = _.merge({}, commonChartConfig, {
    type: "line" as const,
    options: {
        scales: {
            x: {
                time: {
                    minUnit: "day" as const,
                    displayFormats: {
                        day: "MMM d",
                        month: "MMM", // May need year here also (MMM 'yy) as time data accumulates
                        year: "yyyy",
                    },
                    tooltipFormat: "DDD",
                },
            },
        },
    },
});

const commonBarChartConfig = _.merge({}, commonChartConfig, {
    type: "bar" as const,
    options: {
        scales: {
            x: {
                time: {
                    minUnit: "month" as const,
                    displayFormats: {
                        month: "MMM", // May need year here also (MMM 'yy) as time data accumulates
                        year: "yyyy",
                    },
                    tooltipFormat: "MMMM yyyy",
                },
            },
        },
    },
});

const charts = computed(() => {
    let usersChart = null;
    if (usersData.value && !usersData.value.error) {
        usersChart = showCumulativeDailyChart ? createDailyCumulativeUsersChart(usersData.value) : createMonthlyUsersChart(usersData.value);
    }

    let sessionsChart = null;
    if (sessionsData.value && !sessionsData.value.error) {
        sessionsChart = createSessionsChart(sessionsData.value);
    }

    return {
        users: usersChart,
        sessions: sessionsChart,
    };
});

function createDailyCumulativeUsersChart(data: UsersChartData): ChartConfiguration<"line"> {
    return _.merge({}, commonLineChartConfig, {
        data: {
            labels: data.daily_cumulative_counts.map((row) => row.date),
            datasets: [
                {
                    label: "Return Users",
                    data: data.daily_cumulative_counts.map((m) => m.return_users),
                    fill: "origin",
                    borderWidth: 1,
                    borderColor: "#636EFB",
                    // HSLA chosen here to match the perceived design color of #B1B6FD with maximal
                    // transparency on a white background so that grid lines show through.
                    backgroundColor: "hsla(236 95% 54.4% / 0.34)",
                    pointRadius: 0,
                },
                {
                    label: "Active Users",
                    data: data.daily_cumulative_counts.map((m) => m.active_users - m.return_users),
                    fill: "-1",
                    borderWidth: 1,
                    borderColor: "#FEAA59",
                    // HSLA chosen here to match the perceived design color of #FFD3A8 with maximal
                    // transparency on a white background so that grid lines show through.
                    backgroundColor: "hsla(30 100% 54.1% / 0.37)",
                    pointRadius: 0,
                },
            ],
        },
        options: {
            scales: {
                y: {
                    stacked: true,
                },
            },
        },
    });
}

function createMonthlyUsersChart(data: UsersChartData): ChartConfiguration<"bar"> {
    return _.merge({}, commonBarChartConfig, {
        data: {
            labels: data.monthly_counts.map((row) => row.date),
            datasets: [
                {
                    label: "Active Users",
                    data: data.monthly_counts.map((m) => m.active_users),
                    borderWidth: 1,
                    borderColor: "#FEAA59",
                    // HSLA chosen here to match the perceived design color of #FFD3A8 with maximal
                    // transparency on a white background so that grid lines show through.
                    backgroundColor: "hsla(30 100% 54.1% / 0.37)",
                },
            ],
        },
        options: {
            plugins: {
                legend: {
                    display: false,
                },
            },
        },
    });
}

function createSessionsChart(data: SessionsChartData): ChartConfiguration<"line"> {
    return _.merge({}, commonLineChartConfig, {
        data: {
            labels: data.counts.map((row) => row.date),
            datasets: [
                {
                    label: "Cumulative Sessions",
                    data: data.counts.map((m) => m.cumulative_sessions),
                    fill: "origin",
                    borderWidth: 1,
                    borderColor: "#44C1DB",
                    // HSLA chosen here to match the perceived design color of #F086B7 with maximal
                    // transparency on a white background so that grid lines show through.
                    backgroundColor: "hsla(332 77.9% 50.2% / 0.56)",
                    pointRadius: 0,
                },
            ],
        },
        options: {
            plugins: {
                legend: {
                    display: false,
                },
            },
        },
    });
}

const emptyPlaceholderStat: FeedbackStat = {
    title: "",
    value: "",
    caption: "",
};
const loadingPlaceholderStats = [emptyPlaceholderStat, emptyPlaceholderStat, emptyPlaceholderStat];

const emptyPlaceholderQuote = { quote: "" };
const loadingPlaceholderQuotes = [emptyPlaceholderQuote, emptyPlaceholderQuote, emptyPlaceholderQuote];

const { data: feedbackStats, isFetching: isFetchingFeedbackStats } = useFetch("/api/client-analytics/feedback-stats/").json<FeedbackStatsResponse>();
const { data: feedbackQuotes, isFetching: isFetchingFeedbackQuotes } = useFetch("/api/client-analytics/feedback-quotes/").json<FeedbackQuotesResponse>();
const { data: usageInsight, isFetching: isFetchingUsageInsight } = useFetch("/api/client-analytics/nadia-insights/usage").json<NadiaInsightResponse>();
</script>
